import { Member } from "../../model/members/Member";
import { CreateMemberRequest } from "../../requests/member/CreateMemberRequest";
import { SelfServiceCreateMemberRequest } from "../../requests/member/SelfServiceCreateMemberRequest";
import { UpdateMemberRequest } from "../../requests/member/UpdateMemberRequest";
import { MemberProfile } from "../../model/members/MemberProfile";
import { LeadAssignmentNote, LeadAssignmentState } from "../../model/members/StaffTask";
import { MemberConversions } from "../../model/members/MemberConversion";
import { ApiError, ClubBookingApiClient } from "../../common/api/ClubBookingApiClient";
import { PlayingMember } from "../../model/members/PlayingMember";
import { MemberSearchResult } from "../../model/members/MemberSearchResult";
import { MembershipCategory } from "../../model/members/MembershipCategory";
import { MembershipCategoryRow } from "../../model/members/MembershipCategoryRow";
import { MemberMetadataTypeDescriptor } from "../../model/members/MemberMetadataTypeDescriptor";
import { RollOverMembersRequest } from "../../requests/member/RollOverMembersRequest";
import { CreateMemberCancellationRequest } from "../../requests/member/CreateMemberCancellationRequest";
import { MemberCheckinEntry } from "../../model/members/MemberCheckinEntry";
import { CheckinReport } from "../../model/members/CheckinReport";
import { MemberCancellation } from "../../model/members/MemberCancellation";
import { HealthFormSummary, HealthFormReview, HealthAnswer } from "../../model/members/HealthFormSummary";
import { apiConfig } from "../../config/api";

export class MemberService extends ClubBookingApiClient {
    async getMembers(): Promise<Member[]> {
        try {
            console.log('Requesting members for data source');
            return this.get<Member[]>('/members');
        } catch (error) {
            console.error('Error getting members:', error);
            throw error;
        }
    }

    async getPlayingMembers(): Promise<PlayingMember[]> {
        console.log('Requesting playing members');
        return this.get<PlayingMember[]>('/members/playing');
    }

    // Server-side member search for the search-as-you-type UIs. Returns only the
    // ranked matches (slim rows), so we never ship the whole member list. Runs
    // quietly: no global loading flash, no error notification on a transient
    // keystroke failure.
    async searchMembers(term: string, limit: number = 25, activeOnly: boolean = false): Promise<MemberSearchResult[]> {
        return this.get<MemberSearchResult[]>(
            '/members/search',
            { q: term, limit, activeOnly: String(activeOnly) },
            { showLoading: false, showError: false }
        );
    }

    async getMemberProfiles(): Promise<MemberProfile[]> {
        console.log('Requesting member profiles');
        return this.get<MemberProfile[]>('/members');
    }

    /** Every lead in the tenant. `mineOnly` is the "My Leads" filter — the caller's staff
     * id is resolved server-side, so the client never names one. */
    async getRetentionLeads(mineOnly = false): Promise<MemberProfile[]> {
        // A failed GET resolves to undefined rather than rejecting, so without this a 500
        // renders as "no leads" — an empty pipeline and a broken one look identical, and
        // only one of them needs fixing.
        const raw = await this.get<MemberProfile[]>('/members/leads', mineOnly ? { mine: 'true' } : undefined);
        if (raw === undefined) throw new Error('Failed to load leads.');
        return raw;
    }

    /**
     * Assign a batch of leads to a staff member as calls to make. This also makes them the
     * leads' handler — ownership drives the "My Leads" filter and the handler column, not
     * visibility. `assigned` counts leads moved, not call tasks created (re-assigning an
     * already-tasked lead still moves it).
     */
    async assignCalls(assigneeStaffId: number, membershipIds: string[], note?: string | null): Promise<number> {
        const result = await this.post<{ assigned: number; callsCreated: number }>(
            '/dashboard/tasks/assign-calls',
            { assigneeStaffId, membershipIds, note: note?.trim() || null }
        );
        return result?.assigned ?? 0;
    }

    /** The manager's assignment note per lead, for outstanding calls. Keyed by membership id. */
    async getLeadAssignmentNotes(): Promise<LeadAssignmentNote[]> {
        const raw = await this.get<LeadAssignmentNote[]>('/dashboard/tasks/assignment-notes', undefined, {
            showLoading: false,
        });
        return Array.isArray(raw) ? raw : [];
    }

    /**
     * Where each of the caller's own assigned leads stands today. Includes leads already dealt
     * with, which the assignment-notes call deliberately excludes — the CRM needs both to tell
     * "done today" from "never assigned to me".
     */
    async getMyCallAssignmentStates(): Promise<LeadAssignmentState[]> {
        // No date sent on purpose: the server resolves today in the CLUB's timezone, which is
        // what "dealt with today" means here. The browser's day is only a guess at it, and
        // wrong for anyone using the CRM from elsewhere.
        const raw = await this.get<LeadAssignmentState[]>('/dashboard/calls/assignment-states', undefined, {
            showLoading: false,
        });
        return Array.isArray(raw) ? raw : [];
    }

    /**
     * FORT-360: the tenant's lead → member conversions — the counter's number and the list
     * behind it. Degrades to an empty summary rather than throwing: the counter is a badge
     * on the CRM, and losing it must not blank the pipeline it sits above.
     */
    async getConversions(): Promise<MemberConversions> {
        const raw = await this.get<MemberConversions>('/members/conversions', undefined, {
            showLoading: false,
        });
        return raw ?? { totalCount: 0, conversions: [] };
    }

    /**
     * Stamp the logged-in staff member as this lead's handler. The staff id is resolved
     * server-side from the caller, so acting on a lead is what sets the handler — there is
     * no way to nominate someone else.
     */
    async claimLead(membershipId: string): Promise<void> {
        await this.post<void>(`/members/${membershipId}/claim`, undefined, { showSuccess: false, showError: false });
    }

    async getByAuth0Id(auth0Id: string): Promise<MemberProfile> {
        return this.get<MemberProfile>(`/members/byauth0id/${auth0Id}`);
    }

    async getByLegacyId(legacyId: string): Promise<MemberProfile> {
        return this.get<MemberProfile>(`/members/bylegacyid/${legacyId}`);
    }

    async getMembershipCategories(): Promise<MembershipCategory[]> {
        return this.get<MembershipCategory[]>('/members/categories');
    }

    // Current-client categories WITH their id (unlike the name-only getMembershipCategories),
    // used where a numeric member_category id is needed — e.g. the promotion config. FORT-315.
    // rethrow, not showError: this endpoint is gated on CanReadPromotions like GET /promotions,
    // and a swallowed 403 left the promotion modals with an empty category list (FORT-398). Both
    // are loaded together, so notifying here too would stack two toasts on one failed open —
    // the promotions call owns the message, this one just has to fail rather than look empty.
    // Callers must handle the rejection.
    async getMembershipCategoriesDetailed(): Promise<MembershipCategoryRow[]> {
        const rows = await this.get<MembershipCategoryRow[]>(
            '/members/categories/detailed', undefined, { rethrow: true });
        return rows ?? [];
    }

    // Global member-type lookup (e.g. "Member", "Coach") backing the member-type
    // dropdown. Loads quietly so a failure leaves the dropdown empty rather than
    // blocking the whole form.
    async getMemberTypes(): Promise<string[]> {
        return this.get<string[]>(
            '/members/types',
            undefined,
            { showLoading: false, showError: false }
        );
    }

    // Catalogue of member-metadata types driving the staff Metadata section. Loads
    // quietly (no loading flash / error toast): a failure just leaves the section
    // empty rather than blocking the whole profile.
    async getMetadataTypes(): Promise<MemberMetadataTypeDescriptor[]> {
        return this.get<MemberMetadataTypeDescriptor[]>(
            '/members/metadata/types',
            undefined,
            { showLoading: false, showError: false }
        );
    }

    async getHealthSummary(membershipId: string): Promise<HealthFormSummary> {
        return this.get<HealthFormSummary>(`/members/${membershipId}/health`, undefined, { showLoading: false });
    }

    async recordHealthReview(membershipId: string, notes?: string): Promise<HealthFormReview> {
        return this.post<HealthFormReview>(
            `/members/${membershipId}/health/review`,
            { notes: notes ?? null },
            { successMessage: 'Health questionnaire marked as reviewed.' }
        );
    }

    // Submit (or re-submit) a member's health questionnaire. Each call creates a
    // new submission; the latest is what the Health tab shows. Used to complete
    // the questionnaire for members created without one (quick-save/import).
    async submitHealthForm(membershipId: string, answers: HealthAnswer[]): Promise<void> {
        await this.post<void>(
            '/healthform/submit',
            { membershipId, answers },
            { successMessage: 'Health questionnaire saved.' }
        );
    }

    async getMemberPhoto(membershipId: string): Promise<string> {
        return this.get<string>(`/members/photo/${membershipId}`);
    }

    async getMemberPhotoData(membershipId: string): Promise<string> {
        // get() resolves to undefined on request failure (showError is off), so
        // normalize to an empty string to honour the Promise<string> contract and
        // match the backend's "no photo" response. Set showError explicitly so the
        // error-swallowing behaviour this relies on doesn't depend on the default.
        return (await this.get<string>(`/members/photo/${membershipId}/data`, undefined, { showError: false })) ?? '';
    }

    async uploadMemberPhoto(membershipId: string, file: File): Promise<string> {
        const formData = new FormData();
        formData.append('photo', file);
        
        const headers = await this.getHeaders({ skipClientHeader: false });
        headers.delete('Content-Type');

        const response = await fetch(`${apiConfig.apiUrl}/members/photo/upload/${membershipId}`, {
            method: 'POST',
            headers,
            body: formData,
        });

        if (!response.ok) {
            const errorText = await response.text();
            let errorMessage: string;
            try {
                const errorData = JSON.parse(errorText);
                errorMessage = errorData.message || errorData || errorText;
            } catch {
                errorMessage = errorText;
            }
            throw new Error(errorMessage);
        }

        return response.text();
    }

    async create(request: CreateMemberRequest): Promise<MemberProfile> {
        return this.post<MemberProfile>('/members/create', request, {successMessage: 'Member created successfully', showLoading: true});
    }

    async rollOverMembers(request: RollOverMembersRequest): Promise<void> {
        try {
            return this.post<void>('/members/rollover', request, {successMessage: 'Members rolled over successfully', showLoading: true});
        } catch (error) {
            console.error('Error rolling over members:', error);
            throw error;
        }
    }

    async update(request: UpdateMemberRequest): Promise<void> {
        try {
            return this.post('/members/update', request, {successMessage: 'Member updated successfully', showLoading: true});
        } catch (error) {
            console.error('Error updating member:', error);
            throw error;
        }
    }

    async getMember(memberId: string, showError: boolean = true) {
        try {
            return this.get<MemberProfile>(`/members/byid/${memberId}`, undefined, {showError: showError});
        } catch (error) {
            console.error('Error getting member:', error);
            throw error;
        }
    }

    async getMembersByIds(membershipIds: string[]): Promise<MemberProfile[]> {
        try {
            console.log('getMembersByIds called with:', membershipIds);
            console.log('Type of membershipIds:', typeof membershipIds);
            console.log('Is array:', Array.isArray(membershipIds));
            
            const queryParams = membershipIds.map(id => `membershipIds=${id}`).join('&');
            console.log('Generated query params:', queryParams);
            return this.get<MemberProfile[]>(`/members/byids?${queryParams}`);
        } catch (error) {
            console.error('Error getting member:', error);
            throw error;
        }
    }
    
    async cancelMembership(request: CreateMemberCancellationRequest): Promise<void> {
        try {
            return this.post<void>('/members/cancel', request, {successMessage: 'Membership cancellation scheduled successfully', showLoading: true});
        } catch (error) {
            console.error('Error cancelling membership:', error);
            throw error;
        }
    }

    async checkInMember(membershipId: string): Promise<void> {
        try {
            console.log('Checking in member:', membershipId);
            return this.put<void>(`/members/checkin/${membershipId}`, {}, {successMessage: 'Member checked in successfully', showLoading: true});
        } catch (error) {
            console.error('Error checking in member:', error);
            throw error;
        }
    }

    async getCheckinHistory(membershipId: string): Promise<MemberCheckinEntry[]> {
        try {
            console.log('Fetching checkin history for member:', membershipId);
            return this.get<MemberCheckinEntry[]>(`/members/checkin/history/${membershipId}`);
        } catch (error) {
            console.error('Error fetching checkin history:', error);
            throw error;
        }
    }

    // FORT-320: club-wide check-in report for a from/to range (ISO 8601 strings).
    async getClubCheckins(fromIso: string, toIso: string): Promise<CheckinReport> {
        try {
            const params = `from=${encodeURIComponent(fromIso)}&to=${encodeURIComponent(toIso)}`;
            return await this.get<CheckinReport>(`/members/checkins?${params}`, undefined, { showError: true });
        } catch (error) {
            console.error('Error fetching club check-in report:', error);
            throw error;
        }
    }

    async getCancellations(membershipId: string): Promise<MemberCancellation[]> {
        try {
            console.log('Fetching cancellations for member:', membershipId);
            return this.get<MemberCancellation[]>(`/members/cancellations/${membershipId}`);
        } catch (error) {
            console.error('Error fetching cancellations:', error);
            throw error;
        }
    }

    async getMyProfile(): Promise<MemberProfile> {
        return this.get<MemberProfile>('/members/me/profile');
    }

    async createMyProfile(request: SelfServiceCreateMemberRequest): Promise<MemberProfile> {
        return this.post<MemberProfile>('/members/me/create', request, {successMessage: 'Profile created successfully', showLoading: true});
    }

    // `showSuccess: false` lets a caller own the success UX — e.g. the
    // profile-completion wizard saves several sections and raises ONE
    // "Successfully Completed Profile" notification instead of one per call.
    async updateMyProfile(request: UpdateMemberRequest, options?: { showSuccess?: boolean; showError?: boolean }): Promise<void> {
        return this.post('/members/me/update', request, {successMessage: 'Profile updated successfully', showLoading: true, ...options});
    }

    async getMyPhoto(): Promise<string> {
        return this.get<string>('/members/me/photo');
    }

    // The signed-in member's own health summary (latest submission + history +
    // reviews). Scoped server-side to the authenticated member.
    async getMyHealthSummary(): Promise<HealthFormSummary> {
        return this.get<HealthFormSummary>('/members/me/health', undefined, { showLoading: false });
    }

    // The signed-in member submits (or re-submits) their own health questionnaire.
    // The membershipId is server-forced from the token, so only answers are sent.
    // `showSuccess: false` suppresses the per-call toast (see updateMyProfile).
    async submitMyHealthForm(answers: HealthAnswer[], options?: { showSuccess?: boolean; showError?: boolean }): Promise<void> {
        await this.post<void>(
            '/members/me/health/submit',
            { answers },
            { successMessage: 'Health questionnaire saved.', ...options }
        );
    }

    async uploadMyPhoto(file: File): Promise<string> {
        const formData = new FormData();
        formData.append('photo', file);

        const headers = await this.getHeaders({ skipClientHeader: false });
        headers.delete('Content-Type');

        const response = await fetch(`${apiConfig.apiUrl}/members/me/photo/upload`, {
            method: 'POST',
            headers,
            body: formData,
        });

        if (!response.ok) {
            const errorText = await response.text();
            let errorMessage: string;
            try {
                const errorData = JSON.parse(errorText);
                errorMessage = errorData.message || errorData || errorText;
            } catch {
                errorMessage = errorText;
            }
            throw new Error(errorMessage);
        }

        return response.text();
    }

    async exportActiveMembersCsv(): Promise<Blob> {
        const headers = await this.getHeaders();
        const response = await fetch(`${apiConfig.apiUrl}/members/export/active/csv`, { headers });
        if (!response.ok) {
            const errorText = await response.text();
            let errorMessage: string;
            try {
                const errorData = JSON.parse(errorText);
                errorMessage = errorData.message || errorData || errorText;
            } catch {
                errorMessage = errorText;
            }
            throw new ApiError(response.status, errorMessage);
        }
        return response.blob();
    }

    async exportExpiredMembersCsv(): Promise<Blob> {
        const headers = await this.getHeaders();
        const response = await fetch(`${apiConfig.apiUrl}/members/export/expired/csv`, { headers });
        if (!response.ok) {
            const errorText = await response.text();
            let errorMessage: string;
            try {
                const errorData = JSON.parse(errorText);
                errorMessage = errorData.message || errorData || errorText;
            } catch {
                errorMessage = errorText;
            }
            throw new ApiError(response.status, errorMessage);
        }
        return response.blob();
    }
}